import { useState, useMemo, useCallback } from 'react';

export interface Order {
  id: string;
  accessionId: string;
  patientId: string;
  collectionDatetime: string;
  specimenClass: string;
  orderingPhysician: string;
  accessionLocationId: string;
  clinicalHistory: string;
  accessionedBy: string;
  accessionedById: string;
  grossingTechId: string;
  assignedPathologistId: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  amendedFromId?: number;
  amendedAt?: string;
  amendedBy?: string;
  version?: string;
  addendaId?: string;
  orderedBy?: number;
  orderDate?: string;
  createdBy?: string;
  sectionValues?: string;
}

interface Patient {
  id: string; // UUID from GraphQL
  patientId: string;
  name: string;
  dob: string;
  sex?: string;
  mrn?: string;
  locationId?: string;
  createdAt?: string;
}

export const useOrderFilters = (orders: Order[], patients: Patient[]) => {
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [locationFilterAnchorEl, setLocationFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [accessionFilterAnchorEl, setAccessionFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [accessionFilterValue, setAccessionFilterValue] = useState('');
  const [patientFilterAnchorEl, setPatientFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [patientFilterValue, setPatientFilterValue] = useState('');
  const [searchValue, setSearchValue] = useState('');

  // Memoize a map of patientId to last 5 orders for tooltip
  const patientOrdersMap = useMemo(() => {
    const map: Record<string, { accession: string; date: string }[]> = {};
    
    for (const order of orders) {
      const patientKey = String(order.patientId);
      if (!map[patientKey]) map[patientKey] = [];
      map[patientKey].push({ accession: order.accessionId, date: order.collectionDatetime });
    }
    
    for (const pid of Object.keys(map)) {
      if (Array.isArray(map[pid])) {
        map[pid] = map[pid]
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .slice(0, 5);
      } else {
        map[pid] = [];
      }
    }
    return map;
  }, [orders]);

  const filteredOrders = useMemo(() => {
    if (!orders) return [];
    return orders.filter(order => {
      // Find patient for filtering purposes
      const patient = patients.find(p => String(p.id) === String(order.patientId));
      
      // Existing filters
      if (selectedLocationIds.length > 0 && !selectedLocationIds.includes(String(order.accessionLocationId))) return false;
      if (accessionFilterValue && !String(order.accessionId).includes(accessionFilterValue)) return false;
      if (patientFilterValue && patient) {
        const patientName = patient.name;
        const patientMRN = patient.patientId;
        if (!patientName.toLowerCase().includes(patientFilterValue.toLowerCase()) && !String(patientMRN).includes(patientFilterValue)) return false;
      }
      
      // Note: Global search is now handled by API, not client-side filtering
      
      return true;
    });
  }, [orders, selectedLocationIds, accessionFilterValue, patientFilterValue, patients]);

  const sortedOrders = useMemo(() => {
    if (!filteredOrders) return [];
    return [...filteredOrders].sort((a, b) => {
      if (sortColumn === 'status') {
        if (sortDirection === 'asc') return a.status.localeCompare(b.status);
        else return b.status.localeCompare(a.status);
      }
      if (sortColumn === 'accessionId') {
        if (sortDirection === 'asc') return a.accessionId.localeCompare(b.accessionId);
        else return b.accessionId.localeCompare(a.accessionId);
      }
      if (sortColumn === 'collectionDatetime') {
        if (sortDirection === 'asc') return new Date(a.collectionDatetime).getTime() - new Date(b.collectionDatetime).getTime();
        else return new Date(b.collectionDatetime).getTime() - new Date(a.collectionDatetime).getTime();
      }
      if (sortColumn === 'createdAt') {
        if (sortDirection === 'asc') return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        else return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      return 0;
    });
  }, [filteredOrders, sortColumn, sortDirection]);

  const handleSort = useCallback((column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  }, [sortColumn, sortDirection]);

  const clearLocationFilter = useCallback(() => setSelectedLocationIds([]), []);
  const clearAccessionFilter = useCallback(() => setAccessionFilterValue(''), []);
  const clearPatientFilter = useCallback(() => setPatientFilterValue(''), []);
  const clearSearch = useCallback(() => setSearchValue(''), []);

  // Memoize the return object to prevent unnecessary re-renders
  const result = useMemo(() => ({
    sortColumn,
    sortDirection,
    accessionFilterValue,
    setAccessionFilterValue,
    patientFilterValue,
    setPatientFilterValue,
    selectedLocationIds,
    setSelectedLocationIds,
    accessionFilterAnchorEl,
    setAccessionFilterAnchorEl,
    patientFilterAnchorEl,
    setPatientFilterAnchorEl,
    locationFilterAnchorEl,
    setLocationFilterAnchorEl,
    searchValue,
    setSearchValue,
    handleSort,
    clearAccessionFilter,
    clearPatientFilter,
    clearLocationFilter,
    sortedOrders,
    patientOrdersMap,
  }), [
    sortColumn,
    sortDirection,
    accessionFilterValue,
    patientFilterValue,
    selectedLocationIds,
    accessionFilterAnchorEl,
    patientFilterAnchorEl,
    locationFilterAnchorEl,
    searchValue,
    handleSort,
    clearAccessionFilter,
    clearPatientFilter,
    clearLocationFilter,
    sortedOrders,
    patientOrdersMap,
  ]);

  return result;
}; 